import React, { useEffect, useRef, useState, useContext } from "react";
import OTPInput from "react-otp-input";
import QuestContext from "../../../../components/QuestWrapper";
import LoginApi from "./loginApi";
import { OtpVerificationProps } from "./logintypes";
import "./OtpVerification.css";
import Loader from "./Loader";
import { leftArrow2 } from "../../../../assets/images";
import { PrimaryButton } from "../../../../components/Modules/PrimaryButton";

const OtpVerification: React.FC<OtpVerificationProps> = ({
  otpScreen,
  setOtpScreen,
  sendOTP,
  setSendOTP,
  email,
  redirectURL,
  entityId,
  apiKey,
  apiSecret,
  onSubmit,
  onError,
  themeManager,
  campaignId,
  campaignVariationId,
}) => {
  const [otp, setOTP] = useState<string>("");
  const [timeRemaining, setTimeRemaining] = useState<number>(300); // 5 minutes
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const { setUser, apiType } = useContext(QuestContext.Context);

  const loginApi = new LoginApi(apiType);

  const isValidURL = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  }

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (value: string) => {
    setOTP(value);
  };

  const verifyOTP = async () => {
    if (otp.length !== 6) return;

    try {
      setShowLoader(true);
      const response = await loginApi.verifyOTP(email, otp, entityId, {
        apiKey,
        apiSecret,
      }, campaignId, campaignVariationId);

      if (response.success) {
        if (onSubmit) {
          onSubmit({
            userId: response.userId,
            token: response.token,
            newUser : response.newUser,
            userCredentials: { email },
          });
        }

        setUser({
          userId: response.userId,
          token: response.token,
          newUser : response.newUser,
          userCredentials: { email },
        });

        if (isValidURL(redirectURL)) {
          window.location.href = redirectURL;
        }
      } else {
        onError?.({
          email,
          error: response.error || "OTP verification failed",
        });
      }
    } catch (error) {
      console.error(error);
      onError?.({
        email,
        error: "Failed to verify OTP",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const resendOTP = async () => {
    try {
      setShowLoader(true);
      const response = await loginApi.sendOTP(email, entityId, {
        apiKey,
        apiSecret,
      });

      if (response.success) {
        setTimeRemaining(300);
        startTimer();
      }
    } catch (error) {
      console.error(error);
      onError?.({
        email,
        error: "Failed to resend OTP",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const renderLoginIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="52"
      height="52"
      viewBox="0 0 52 52"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.4603 11.6605C5 14.1208 5 18.0806 5 26.0002C5 33.9198 5 37.8796 7.4603 40.3399C9.92061 42.8002 13.8804 42.8002 21.8 42.8002H30.2C38.1196 42.8002 42.0794 42.8002 44.5397 40.3399C47 37.8796 47 33.9198 47 26.0002C47 18.0806 47 14.1208 44.5397 11.6605C42.0794 9.2002 38.1196 9.2002 30.2 9.2002H21.8C13.8804 9.2002 9.92061 9.2002 7.4603 11.6605ZM39.8099 16.5919C40.3668 17.2601 40.2765 18.2533 39.6083 18.8101L34.9957 22.654C33.1343 24.2052 31.6257 25.4624 30.2941 26.3188C28.9071 27.2109 27.5563 27.7744 26 27.7744C24.4437 27.7744 23.0929 27.2109 21.7059 26.3188C20.3743 25.4624 18.8657 24.2052 17.0043 22.654L12.3917 18.8101C11.7235 18.2533 11.6332 17.2601 12.1901 16.5919C12.7469 15.9237 13.7401 15.8334 14.4083 16.3902L18.942 20.1683C20.9012 21.801 22.2614 22.9309 23.4098 23.6695C24.5215 24.3844 25.2753 24.6244 26 24.6244C26.7246 24.6244 27.4785 24.3844 28.5902 23.6695C29.7386 22.9309 31.0988 21.801 33.058 20.1683L37.5917 16.3902C38.2599 15.8334 39.2531 15.9237 39.8099 16.5919Z"
        fill={themeManager.getIconStyles().color}
      />
    </svg>
  );


  return (
    <div className="questLabs">
      {showLoader && <Loader />}

      <div className="embeded-otp">
        <div className="q_heading_cont">
          <img
            onClick={() => {
              setSendOTP(!sendOTP);
              setOtpScreen(!otpScreen);
            }}
            src={leftArrow2}
            alt="back"
          />
          <div className="q-login-head2" style={themeManager.getHeadingStyles()}>
            Confirm verification code
          </div>
        </div>

        <div className="q_outer_cont">
          <div
            className="q_otp_main_cont"
            style={{ background: themeManager.getIconStyles().borderColor }}
          >
            <div
              className="q_otp_cont"
              style={{ background: themeManager.getIconStyles().background }}
            >
              {renderLoginIcon()}
            </div>
          </div>
        </div>

        <div>
          <div className="q-resend" style={themeManager.getDescriptionStyles()}>
            We've sent a verification code to
          </div>
          <p className="q_email_otp" style={themeManager.getHeadingStyles()}>
            {email}
          </p>

          <div style={{ marginTop: "20px" }}>
            <OTPInput
              value={otp}
              onChange={handleOtpChange}
              numInputs={6}
              inputStyle="q-inputStyle"
              containerStyle="q-containerStyle"
              renderInput={(props) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <input {...props} placeholder="-" style={themeManager.getOtpInputStyles()} />
                </div>
              )}
            />

            {otp.length > 0 && otp.length < 6 && (
              <div className="q-login-p">Please enter a valid OTP</div>
            )}
          </div>

          <p className="q_otp_resend" style={themeManager.getDescriptionStyles()}>
            {timeRemaining > 0 ? (
              <>Time remaining: {formatTime(timeRemaining)}</>
            ) : (
              <>
                Did not receive your code?{" "}
                <span
                  style={{
                    color: themeManager.getHeadingStyles().color,
                    cursor: "pointer",
                  }}
                  onClick={resendOTP}
                >
                  Resend
                </span>
              </>
            )}
          </p>
        </div>

        <div className="q_otp_btn_continue">
          <PrimaryButton
            style={themeManager.getPrimaryButtonStyles()}
            onClick={verifyOTP}
          >
            Continue
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;